import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Plus, Trash2, MapPin, DoorOpen, Wifi, ParkingCircle, Mail, Phone, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

interface VenueContact {
  id?: string;
  name: string;
  email: string;
  phone: string;
  role: string;
}

export default function VenueSection({ eventId, permissions }: { eventId: string; permissions: any }) {
  const [venues, setVenues] = useState<any[]>([]);
  const [venueContacts, setVenueContacts] = useState<{ [venueId: string]: VenueContact[] }>({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    load_in_door: '',
    parking_info: '',
    wifi_network: '',
    wifi_password: '',
    notes: ''
  });
  const [contacts, setContacts] = useState<VenueContact[]>([
    { name: '', email: '', phone: '', role: '' }
  ]);

  useEffect(() => {
    loadVenues();
  }, [eventId]);

  const loadVenues = async () => {
    const { data } = await supabase
      .from('venues')
      .select('*')
      .eq('event_id', eventId)
      .order('name');

    if (data) {
      setVenues(data);

      const contactsMap: { [venueId: string]: VenueContact[] } = {};
      for (const venue of data) {
        const { data: contactsData } = await supabase
          .from('venue_contacts')
          .select('*')
          .eq('venue_id', venue.id)
          .order('name');
        if (contactsData) {
          contactsMap[venue.id] = contactsData;
        }
      }
      setVenueContacts(contactsMap);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let venueId = editingId;

    if (editingId) {
      await supabase
        .from('venues')
        .update(formData)
        .eq('id', editingId);
    } else {
      const { data, error } = await supabase
        .from('venues')
        .insert({ ...formData, event_id: eventId })
        .select()
        .single();

      if (data) venueId = data.id;
    }

    if (venueId) {
      if (editingId) {
        await supabase
          .from('venue_contacts')
          .delete()
          .eq('venue_id', venueId);
      }

      const validContacts = contacts.filter(c => c.name.trim() !== '');
      if (validContacts.length > 0) {
        await supabase
          .from('venue_contacts')
          .insert(
            validContacts.map(c => ({
              venue_id: venueId,
              name: c.name,
              email: c.email || null,
              phone: c.phone || null,
              role: c.role || null
            }))
          );
      }
    }

    resetForm();
    loadVenues();
  };

  const deleteEntry = async (id: string) => {
    await supabase.from('venues').delete().eq('id', id);
    setDeleteId(null);
    loadVenues();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      load_in_door: '',
      parking_info: '',
      wifi_network: '',
      wifi_password: '',
      notes: ''
    });
    setContacts([{ name: '', email: '', phone: '', role: '' }]);
    setShowForm(false);
    setEditingId(null);
  };

  const editEntry = (entry: any) => {
    setFormData({
      name: entry.name || '',
      address: entry.address || '',
      load_in_door: entry.load_in_door || '',
      parking_info: entry.parking_info || '',
      wifi_network: entry.wifi_network || '',
      wifi_password: entry.wifi_password || '',
      notes: entry.notes || ''
    });

    const existingContacts = venueContacts[entry.id] || [];
    if (existingContacts.length > 0) {
      setContacts(existingContacts.map(c => ({
        id: c.id,
        name: c.name,
        email: c.email || '',
        phone: c.phone || '',
        role: c.role || ''
      })));
    } else {
      setContacts([{ name: '', email: '', phone: '', role: '' }]);
    }

    setEditingId(entry.id);
    setShowForm(true);
  };

  const addContact = () => {
    setContacts([...contacts, { name: '', email: '', phone: '', role: '' }]);
  };

  const removeContact = (index: number) => {
    if (contacts.length > 1) {
      setContacts(contacts.filter((_, i) => i !== index));
    }
  };

  const updateContact = (index: number, field: keyof VenueContact, value: string) => {
    const updated = [...contacts];
    updated[index] = { ...updated[index], [field]: value };
    setContacts(updated);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Venue Information</h2>
          <p className="text-zinc-500 mt-1">Location details and logistics</p>
        </div>
        {permissions.canEdit && (
          <Button onClick={() => setShowForm(!showForm)} className="bg-white text-zinc-900 hover:bg-zinc-200">
            <Plus className="w-4 h-4 mr-2" />
            Add Venue
          </Button>
        )}
      </div>

      {showForm && (
        <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingId ? 'Edit' : 'Add'} Venue
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-zinc-300">Venue Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
              />
            </div>

            <div>
              <Label htmlFor="address" className="text-zinc-300">Address</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                rows={2}
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="load_in_door" className="text-zinc-300">Load-In Door</Label>
                <Input
                  id="load_in_door"
                  value={formData.load_in_door}
                  onChange={(e) => setFormData({ ...formData, load_in_door: e.target.value })}
                  placeholder="e.g., Dock 3, Stage Door"
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="parking_info" className="text-zinc-300">Parking Info</Label>
                <Input
                  id="parking_info"
                  value={formData.parking_info}
                  onChange={(e) => setFormData({ ...formData, parking_info: e.target.value })}
                  placeholder="e.g., Lot B, validation required"
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="wifi_network" className="text-zinc-300">WiFi Network</Label>
                <Input
                  id="wifi_network"
                  value={formData.wifi_network}
                  onChange={(e) => setFormData({ ...formData, wifi_network: e.target.value })}
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label htmlFor="wifi_password" className="text-zinc-300">WiFi Password</Label>
                <Input
                  id="wifi_password"
                  value={formData.wifi_password}
                  onChange={(e) => setFormData({ ...formData, wifi_password: e.target.value })}
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300">Venue Contacts</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={addContact}
                  className="text-primary-400 hover:text-primary-300 h-8"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Contact
                </Button>
              </div>
              {contacts.map((contact, index) => (
                <div key={index} className="p-4 bg-zinc-800/30 border border-zinc-700/50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-zinc-400">Contact {index + 1}</span>
                    {contacts.length > 1 && (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => removeContact(index)}
                        className="h-7 w-7 text-zinc-500 hover:text-red-400"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor={`contact_name_${index}`} className="text-xs text-zinc-400">Name</Label>
                      <Input
                        id={`contact_name_${index}`}
                        value={contact.name}
                        onChange={(e) => updateContact(index, 'name', e.target.value)}
                        placeholder="Contact name"
                        className="mt-1 bg-zinc-800/50 border-zinc-700 text-white h-9"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`contact_role_${index}`} className="text-xs text-zinc-400">Role</Label>
                      <Input
                        id={`contact_role_${index}`}
                        value={contact.role}
                        onChange={(e) => updateContact(index, 'role', e.target.value)}
                        placeholder="e.g., Venue Manager"
                        className="mt-1 bg-zinc-800/50 border-zinc-700 text-white h-9"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor={`contact_email_${index}`} className="text-xs text-zinc-400">Email</Label>
                      <Input
                        id={`contact_email_${index}`}
                        type="email"
                        value={contact.email}
                        onChange={(e) => updateContact(index, 'email', e.target.value)}
                        placeholder="contact@venue.com"
                        className="mt-1 bg-zinc-800/50 border-zinc-700 text-white h-9"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`contact_phone_${index}`} className="text-xs text-zinc-400">Phone</Label>
                      <Input
                        id={`contact_phone_${index}`}
                        type="tel"
                        value={contact.phone}
                        onChange={(e) => updateContact(index, 'phone', e.target.value)}
                        placeholder="(555) 123-4567"
                        className="mt-1 bg-zinc-800/50 border-zinc-700 text-white h-9"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div>
              <Label htmlFor="notes" className="text-zinc-300">Additional Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" className="bg-white text-zinc-900 hover:bg-zinc-200">
                {editingId ? 'Update' : 'Add'} Venue
              </Button>
              <Button type="button" variant="ghost" onClick={resetForm} className="text-zinc-400 hover:text-white">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-zinc-500">Loading...</div>
      ) : venues.length === 0 ? (
        <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-8 text-center">
          <p className="text-zinc-500">No venues added yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {venues.map((venue) => (
            <div key={venue.id} className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-6 hover:border-zinc-700/50 transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-medium text-white mb-1">{venue.name}</h3>
                  {venue.address && (
                    <p className="text-zinc-400 text-sm whitespace-pre-wrap">{venue.address}</p>
                  )}
                </div>
                {permissions.canEdit && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => editEntry(venue)}
                      className="text-zinc-400 hover:text-white h-8"
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteId(venue.id)}
                      className="text-zinc-400 hover:text-red-400 h-8 w-8"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {venue.load_in_door && (
                  <div className="flex items-start gap-3 text-sm">
                    <DoorOpen className="w-4 h-4 text-zinc-500 mt-0.5" />
                    <div>
                      <p className="text-zinc-500">Load-In Door</p>
                      <p className="text-zinc-300">{venue.load_in_door}</p>
                    </div>
                  </div>
                )}
                {venue.parking_info && (
                  <div className="flex items-start gap-3 text-sm">
                    <ParkingCircle className="w-4 h-4 text-zinc-500 mt-0.5" />
                    <div>
                      <p className="text-zinc-500">Parking</p>
                      <p className="text-zinc-300">{venue.parking_info}</p>
                    </div>
                  </div>
                )}
                {venue.wifi_network && (
                  <div className="flex items-start gap-3 text-sm">
                    <Wifi className="w-4 h-4 text-zinc-500 mt-0.5" />
                    <div>
                      <p className="text-zinc-500">WiFi</p>
                      <p className="text-zinc-300">
                        {venue.wifi_network}
                        {venue.wifi_password && <span className="text-zinc-500"> / {venue.wifi_password}</span>}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {venueContacts[venue.id] && venueContacts[venue.id].length > 0 && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <p className="text-sm font-medium text-zinc-400 mb-3">Venue Contacts</p>
                  <div className="space-y-3">
                    {venueContacts[venue.id].map((contact) => (
                      <div key={contact.id} className="bg-zinc-800/30 rounded-lg p-3">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-primary-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="font-medium text-white text-sm">{contact.name}</p>
                                {contact.role && (
                                  <p className="text-xs text-zinc-500 mt-0.5">{contact.role}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                              {contact.email && (
                                <a
                                  href={`mailto:${contact.email}`}
                                  className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-primary-400 transition-colors"
                                >
                                  <Mail className="w-3 h-3" />
                                  <span>{contact.email}</span>
                                </a>
                              )}
                              {contact.phone && (
                                <a
                                  href={`tel:${contact.phone}`}
                                  className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-primary-400 transition-colors"
                                >
                                  <Phone className="w-3 h-3" />
                                  <span>{contact.phone}</span>
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {venue.notes && (
                <p className="text-sm text-zinc-500 mt-4 pt-4 border-t border-zinc-800">
                  {venue.notes}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Venue?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently remove this venue information.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteId(null)} className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteEntry(deleteId)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
